// Paramètres et fonctionnalités (G04, charte D-038) : réglages regroupés par thème, valeur en vigueur et son
// origine, changements programmés signalés ; chaque modification porte une portée, une date d'effet et un
// motif, et l'historique est consultable à côté de la valeur.

import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CODES_PROVINCES } from '@carburanttrack/shared';
import { api } from '../../api/client';
import type { Parametre } from '../../api/types';
import { aUnRole, useSession } from '../../app/session';
import { Message, MessageErreur, Squelette } from '../../composants/Etats';
import { dateHeure, libelleProvince } from '../../composants/format';
import { CadreAdministration } from './Cadre';

const SECTIONS: { id: string; titre: string; categories: string[] }[] = [
  { id: 'seuils', titre: 'Seuils de situation', categories: ['cartographie'] },
  { id: 'fraicheur', titre: 'Fraîcheur des données et échéances', categories: ['fraicheur', 'echeances'] },
  { id: 'alertes', titre: 'Alertes', categories: ['alertes'] },
  { id: 'tolerances', titre: 'Tolérances, référentiel et conservation', categories: ['tolerances', 'referentiel', 'conservation'] },
  { id: 'circuits', titre: 'Circuits de validation et contrôles de cohérence', categories: ['circuits', 'controles'] },
  { id: 'autonomie', titre: 'Autonomie et consommation', categories: ['autonomie'] },
  { id: 'catalogue', titre: 'Catalogue des produits', categories: ['catalogue'] },
  { id: 'transport', titre: 'Transport et livraisons', categories: ['transport'] },
  { id: 'fonctions', titre: 'Fonctionnalités et environnement', categories: ['fonctionnalites', 'environnement'] },
];

/** Titre d'une catégorie de paramètres que les sections ci-dessus ne connaissent pas encore (jamais le code brut). */
export function libelleCategorie(c: string): string {
  const texte = c.replace(/_/g, ' ').trim();
  return texte.charAt(0).toUpperCase() + texte.slice(1);
}

const NIVEAUX_ALERTE: { cle: string; libelle: string; ton: string }[] = [
  { cle: 'critique', libelle: 'Critique', ton: 'rouge' },
  { cle: 'elevee', libelle: 'Élevée', ton: 'ambre' },
  { cle: 'preventive', libelle: 'Préventive', ton: '' },
  { cle: 'informative', libelle: 'Informative', ton: '' },
];

const LIBELLES_PORTEES: Record<string, string> = { nationale: 'Nationale', province: 'Province', type_installation: 'Type d’installation', organisation: 'Organisation', produit: 'Produit' };

const NOMBRE_DECIMAL = /^-?\d+(\.\d+)?$/;

/**
 * Valeur d'un paramètre en texte lisible : un objet est rendu clé par clé, et un objet imbriqué (par exemple les
 * coefficients par produit) l'est à son tour entre parenthèses, jamais par « [object Object] » ; les nombres,
 * y compris les chaînes décimales, prennent la virgule.
 */
export function afficherValeur(v: unknown): string {
  if (Array.isArray(v)) return v.map((x) => afficherValeur(x)).join(', ');
  if (v !== null && typeof v === 'object') {
    const entrees = Object.entries(v as Record<string, unknown>);
    const imbrique = entrees.some(([, x]) => x !== null && typeof x === 'object');
    return entrees.map(([k, x]) => `${k.replace(/_/g, ' ')} : ${x !== null && typeof x === 'object' && !Array.isArray(x) ? `(${afficherValeur(x)})` : afficherValeur(x)}`).join(imbrique ? ' ; ' : ', ');
  }
  if (typeof v === 'boolean') return v ? 'Activée' : 'Désactivée';
  if (typeof v === 'number') return String(v).replace('.', ',');
  if (typeof v === 'string') return NOMBRE_DECIMAL.test(v) ? v.replace('.', ',') : v;
  if (v === null || v === undefined) return 'aucune';
  return JSON.stringify(v);
}

function ValeurParametre({ p }: { p: Parametre }) {
  const v = p.valeurEffective;
  if (p.type === 'booleen') return <span className={v ? 'etiquette vert' : 'etiquette'}>{v ? 'Activée' : 'Désactivée'}</span>;
  if (p.type === 'json' && v && typeof v === 'object' && NIVEAUX_ALERTE.every((n) => n.cle in (v as Record<string, unknown>))) {
    const o = v as Record<string, number>;
    return (
      <div className="niveaux-alerte">
        {NIVEAUX_ALERTE.map((n) => (
          <div key={n.cle} className={n.ton}>
            <span>{n.libelle}</span>
            <b className="valeur">{o[n.cle]} h</b>
          </div>
        ))}
      </div>
    );
  }
  return <b className={p.type === 'texte' ? 'valeur-texte' : 'valeur valeur-reglage'}>{afficherValeur(v)}</b>;
}

export function AdministrationParametres() {
  const { etat } = useSession();
  const params = useQuery({ queryKey: ['parametres'], queryFn: async () => (await api<Parametre[]>('/api/v1/parametres')).donnees });
  const [ouvert, setOuvert] = useState<string | null>(null);
  const modifiable = aUnRole(etat, 'administrateur_national');
  const donnees = params.data ?? [];
  const connues = new Set(SECTIONS.flatMap((s) => s.categories));
  const autres = [...new Set(donnees.map((p) => p.categorie))].filter((c) => !connues.has(c));
  const sections = [...SECTIONS, ...autres.map((c) => ({ id: `cat-${c}`, titre: libelleCategorie(c), categories: [c] }))]
    .map((s) => ({ ...s, parametres: donnees.filter((p) => s.categories.includes(p.categorie)) }))
    .filter((s) => s.parametres.length > 0);
  const programmes = donnees.filter((p) => p.programmees.length > 0).length;

  return (
    <CadreAdministration
      titre="Paramètres"
      description="Chaque modification porte une portée, une date d’effet et un motif ; l’historique est conservé et l’opération est inscrite au journal d’audit."
      ancres={sections.map((s) => ({ id: s.id, libelle: s.titre }))}
    >
      {params.isLoading && <Squelette lignes={10} />}
      {params.error && <MessageErreur erreur={params.error} />}
      {programmes > 0 && (
        <p className="note ambre">
          {programmes} réglage{programmes > 1 ? 's ont' : ' a'} un changement programmé à une date d’effet future.
        </p>
      )}
      {sections.map((s) => (
        <section key={s.id} id={s.id} className="carte-bloc section-reglages" aria-labelledby={`titre-${s.id}`}>
          <h2 id={`titre-${s.id}`}>{s.titre}</h2>
          {s.parametres.map((p) => (
            <div key={p.cle} className="reglage">
              <div className="reglage-libelle">
                <b>{p.libelle}</b>
                <span className="attenue">{p.description}</span>
              </div>
              <div className="reglage-valeur">
                <div className="reglage-etat">
                  <ValeurParametre p={p} />
                  <small className="attenue">{p.origine === 'defaut' ? 'Valeur par défaut' : `En vigueur depuis le ${dateHeure(p.dateEffet)}`}</small>
                  {p.programmees.map((x) => (
                    <span key={`${x.dateEffet}-${x.porteeValeur ?? ''}`} className="etiquette ambre">
                      Programmé : {afficherValeur(x.valeur)} le {dateHeure(x.dateEffet)}
                      {x.porteeValeur ? `, ${LIBELLES_PORTEES[x.porteeType] ?? x.porteeType} ${x.porteeType === 'province' ? libelleProvince(x.porteeValeur) : x.porteeValeur}` : ''}
                    </span>
                  ))}
                </div>
                <button type="button" className="bouton" aria-expanded={ouvert === p.cle} aria-controls={`edition-${p.cle}`} onClick={() => setOuvert(ouvert === p.cle ? null : p.cle)}>
                  {ouvert === p.cle ? 'Fermer' : modifiable ? 'Modifier, historique' : 'Historique'}
                </button>
              </div>
              {ouvert === p.cle && (
                <div id={`edition-${p.cle}`} className="reglage-edition">
                  <EditionParametre parametre={p} modifiable={modifiable} />
                </div>
              )}
            </div>
          ))}
        </section>
      ))}
    </CadreAdministration>
  );
}

interface LigneHistorique {
  id: string;
  porteeType: string;
  porteeValeur: string | null;
  valeur: unknown;
  valeurPrecedente: unknown;
  dateEffet: string;
  motif: string;
  creeLe: string;
  auteurPrenom: string | null;
  auteurNom: string | null;
}

function EditionParametre({ parametre, modifiable }: { parametre: Parametre; modifiable: boolean }) {
  const client = useQueryClient();
  const historique = useQuery({ queryKey: ['parametre-historique', parametre.cle], queryFn: async () => (await api<LigneHistorique[]>(`/api/v1/parametres/${parametre.cle}/historique`)).donnees });
  const [valeur, setValeur] = useState(parametre.type === 'json' ? JSON.stringify(parametre.valeurEffective) : String(parametre.valeurEffective));
  const [porteeType, setPorteeType] = useState(parametre.portees[0] ?? 'nationale');
  const [porteeValeur, setPorteeValeur] = useState('');
  const [dateEffet, setDateEffet] = useState('');
  const [motif, setMotif] = useState('');
  const [erreurSaisie, setErreurSaisie] = useState<string | null>(null);
  const envoi = useMutation({
    mutationFn: () => {
      let v: unknown = valeur;
      if (parametre.type === 'booleen') v = valeur === 'true';
      if (parametre.type === 'nombre') v = Number(valeur.replace(',', '.'));
      if (parametre.type === 'json') v = JSON.parse(valeur);
      return api(`/api/v1/parametres/${parametre.cle}`, {
        corps: { valeur: v, portee: { type: porteeType, valeur: porteeType === 'nationale' ? null : porteeValeur }, motif, ...(dateEffet ? { dateEffet: new Date(dateEffet).toISOString() } : {}) },
      });
    },
    onSuccess: async () => {
      setMotif('');
      await client.invalidateQueries({ queryKey: ['parametres'] });
      await client.invalidateQueries({ queryKey: ['parametre-historique', parametre.cle] });
      await client.invalidateQueries({ queryKey: ['fonctionnalites'] });
    },
  });
  return (
    <div>
      {modifiable && (
        <form
          onSubmit={(e) => {
            e.preventDefault();
            setErreurSaisie(null);
            if (parametre.type === 'json') {
              try {
                JSON.parse(valeur);
              } catch {
                setErreurSaisie('Valeur JSON invalide');
                return;
              }
            }
            envoi.mutate();
          }}
        >
          <div className="formulaire-ligne">
            <div className="champ">
              <label htmlFor={`v-${parametre.cle}`}>Nouvelle valeur</label>
              {parametre.type === 'booleen' ? (
                <select id={`v-${parametre.cle}`} value={valeur} onChange={(e) => setValeur(e.target.value)}>
                  <option value="true">Activée</option>
                  <option value="false">Désactivée</option>
                </select>
              ) : (
                <input id={`v-${parametre.cle}`} value={valeur} onChange={(e) => setValeur(e.target.value)} />
              )}
            </div>
            <div className="champ">
              <label htmlFor={`p-${parametre.cle}`}>Portée</label>
              <select id={`p-${parametre.cle}`} value={porteeType} onChange={(e) => setPorteeType(e.target.value)}>
                {parametre.portees.map((p) => (
                  <option key={p} value={p}>
                    {LIBELLES_PORTEES[p] ?? p}
                  </option>
                ))}
              </select>
            </div>
            {porteeType !== 'nationale' && (
              <div className="champ">
                <label htmlFor={`pv-${parametre.cle}`}>Valeur de la portée</label>
                {porteeType === 'province' ? (
                  <select id={`pv-${parametre.cle}`} value={porteeValeur} onChange={(e) => setPorteeValeur(e.target.value)}>
                    <option value="">Choisir</option>
                    {CODES_PROVINCES.map((c) => (
                      <option key={c} value={c}>
                        {libelleProvince(c)}
                      </option>
                    ))}
                  </select>
                ) : (
                  <input id={`pv-${parametre.cle}`} value={porteeValeur} onChange={(e) => setPorteeValeur(e.target.value)} />
                )}
              </div>
            )}
            <div className="champ">
              <label htmlFor={`d-${parametre.cle}`}>Date d’effet (vide : immédiate)</label>
              <input id={`d-${parametre.cle}`} type="datetime-local" value={dateEffet} onChange={(e) => setDateEffet(e.target.value)} />
            </div>
          </div>
          <div className="champ">
            <label htmlFor={`m-${parametre.cle}`}>Motif (obligatoire)</label>
            <input id={`m-${parametre.cle}`} value={motif} onChange={(e) => setMotif(e.target.value)} />
          </div>
          {erreurSaisie && <Message type="erreur">{erreurSaisie}</Message>}
          {envoi.error && <MessageErreur erreur={envoi.error} titre="Modification refusée" />}
          {envoi.isSuccess && <Message type="succes">Modification enregistrée avec sa date d’effet.</Message>}
          <button type="submit" className="bouton primaire" disabled={motif.trim().length < 3 || envoi.isPending}>
            Enregistrer
          </button>
        </form>
      )}
      <h3 className="espace-haut">Historique</h3>
      {historique.isLoading && <Squelette lignes={2} />}
      {historique.data?.length === 0 && <p className="attenue">Aucune modification : valeur par défaut.</p>}
      {historique.data && historique.data.length > 0 && (
        <div className="tableau-conteneur">
          <table className="tableau">
            <thead>
              <tr>
                <th scope="col">Date d’effet</th>
                <th scope="col">Valeur</th>
                <th scope="col" className="masquable">
                  Précédente
                </th>
                <th scope="col" className="masquable">
                  Portée
                </th>
                <th scope="col">Motif</th>
                <th scope="col" className="masquable">
                  Auteur
                </th>
              </tr>
            </thead>
            <tbody>
              {historique.data.map((h) => (
                <tr key={h.id}>
                  <td>{dateHeure(h.dateEffet)}</td>
                  <td>{afficherValeur(h.valeur)}</td>
                  <td className="masquable">{afficherValeur(h.valeurPrecedente)}</td>
                  <td className="masquable">
                    {LIBELLES_PORTEES[h.porteeType] ?? h.porteeType}
                    {h.porteeValeur ? ` ${h.porteeType === 'province' ? libelleProvince(h.porteeValeur) : h.porteeValeur}` : ''}
                  </td>
                  <td>{h.motif}</td>
                  <td className="masquable">
                    {h.auteurPrenom} {h.auteurNom}
                    <small>{dateHeure(h.creeLe)}</small>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
